/* React */
import { useEffect, useState } from 'react'

/* Redux */
import { useSelector } from 'src/redux/hooks'

/* Material */
import useStyles from './styles'

/* Components */
import Header from './Header/Header'
import Navigation from './Navigation/Navigation'
import NavigationDrawer from './Navigation/Drawer'
import Footer from './Footer/Footer'
import CookieBanner from './CookieBanner'
import Loading from './Loading'

/* Helpers */
import { getPageChrome, getPageChromeCssVars } from 'src/pageBuilder/config'
import { MenuPageBuilder } from 'src/shared/dbTypes/menuPageBuilder'
import {
  fetchSiteShopSettings,
  readCachedSitePageBuilder,
  writeCachedSitePageBuilder,
} from 'src/pageBuilder/sitePageBuilder'
import {
  mergeLandingPageImageAiGenerated,
  type LandingPageImageAiGenerated,
} from 'src/functions/landingPageImageAiGenerated'

const readInitialSitePageBuilderState = (
  routeSitePageBuilder?: MenuPageBuilder,
  landingPageImageAiGenerated?: LandingPageImageAiGenerated,
) => {
  const cachedBuilder = readCachedSitePageBuilder()

  return {
    builder: routeSitePageBuilder || cachedBuilder,
    landingPageImageAiGenerated,
    resolved: Boolean(routeSitePageBuilder || cachedBuilder),
  }
}

const Layout: React.FC<{children?: React.ReactNode, pageProps?: Record<string, unknown>}> = ({ children, pageProps }) => {
  const routePageBuilder = pageProps?.pageBuilder as MenuPageBuilder | undefined
  const routeSitePageBuilder = pageProps?.sitePageBuilder as MenuPageBuilder | undefined
  const routeLandingPageImageAiGenerated = pageProps?.landingPageImageAiGenerated as LandingPageImageAiGenerated | undefined
  const [clientSitePageBuilderState, setClientSitePageBuilderState] = useState(() =>
    readInitialSitePageBuilderState(routeSitePageBuilder, routeLandingPageImageAiGenerated)
  )
  const pageBuilder = routePageBuilder
  const clientSitePageBuilder = clientSitePageBuilderState.builder
  const chromeBuilder = clientSitePageBuilder
    || routeSitePageBuilder
    || routePageBuilder
  const landingPageImageAiGenerated = mergeLandingPageImageAiGenerated(
    routeLandingPageImageAiGenerated,
    clientSitePageBuilderState.landingPageImageAiGenerated,
  )
  const pageChrome = getPageChrome(chromeBuilder)
  const hasSeparateTopNavigation = pageChrome.builderEnabled
    && pageChrome.navigationPosition === 'top'
    && pageChrome.navigationDisplay === 'separate'
  const waitingForSiteChrome = !routeSitePageBuilder
    && !routePageBuilder
    && !clientSitePageBuilderState.resolved
  const classes = useStyles(pageChrome)

  const loading = useSelector(store => store.App.loading)

  useEffect(() => {
    if (routeSitePageBuilder) {
      writeCachedSitePageBuilder(routeSitePageBuilder)
      setClientSitePageBuilderState({
        builder: routeSitePageBuilder,
        landingPageImageAiGenerated: routeLandingPageImageAiGenerated,
        resolved: true,
      })
      return
    } else {
      const cachedBuilder = readCachedSitePageBuilder()
      setClientSitePageBuilderState({
        builder: cachedBuilder,
        landingPageImageAiGenerated: routeLandingPageImageAiGenerated,
        resolved: Boolean(cachedBuilder || routePageBuilder),
      })
    }

    const controller = new AbortController()
    fetchSiteShopSettings({ signal: controller.signal })
      .then(settings => {
        const nextBuilder = settings.sitePageBuilder || routeSitePageBuilder
        writeCachedSitePageBuilder(nextBuilder)
        setClientSitePageBuilderState({
          builder: nextBuilder,
          landingPageImageAiGenerated: mergeLandingPageImageAiGenerated(
            routeLandingPageImageAiGenerated,
            settings.landingPageImageAiGenerated,
          ),
          resolved: true,
        })
      })
      .catch(() => {
        setClientSitePageBuilderState(current => ({
          ...current,
          resolved: true,
        }))
      })

    return () => controller.abort()
  }, [routeLandingPageImageAiGenerated, routePageBuilder, routeSitePageBuilder])

  return <div className={[
    classes.layout,
    pageChrome.builderEnabled ? 'builder-enabled' : 'legacy-layout',
    waitingForSiteChrome ? 'chrome-loading' : null,
    loading ? 'loading' : null
  ].join(' ')}
    style={getPageChromeCssVars(chromeBuilder)}
  >

    <div className={classes.header}>
      <Header
        pageChrome={pageChrome}
        pageBuilder={chromeBuilder}
        landingPageImageAiGenerated={landingPageImageAiGenerated}
      />
    </div>

    {hasSeparateTopNavigation
      ? <div className={classes.topNavigation} />
      : <nav className={classes.navigation}> {/* TODO: Lighthouse Mobile */}
          <Navigation placement='layout' />
        </nav>
    }

    <NavigationDrawer />

    <main className={classes.main}>
      {children}
    </main>

    <footer className={classes.footer}>
      <Footer
        pageBuilder={chromeBuilder}
        landingPageImageAiGenerated={landingPageImageAiGenerated}
      />
    </footer>

    <CookieBanner />

    {(waitingForSiteChrome || loading) && <Loading />}

  </div>
}

export default Layout
